import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  CannibalizationResult,
  EvaluatedOpportunity,
} from '@/api/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { SidebarLayout, SidebarItem } from '@/components/layout/SidebarLayout';
import { InsightCard, MetricCard } from '@/components/story/StorySection';
import { OpportunityCard, OpportunityCardGrid } from '@/components/ui/OpportunityCard';
import type { StatusType } from '@/components/ui/StatusBadge';

// Build sidebar items for Implications - Phase 7 structure
function buildSidebarItems(language: string): SidebarItem[] {
  const t = language === 'es';
  return [
    {
      id: 'market-segment',
      icon: <span className="text-lg">🎯</span>,
      name: t ? 'Mercado y Segmento' : 'Market & Segment',
      description: t ? 'Oportunidades de mercado' : 'Market opportunities',
    },
    {
      id: 'portfolio',
      icon: <span className="text-lg">📦</span>,
      name: 'Portfolio',
      description: t ? 'Optimización de portafolio' : 'Portfolio optimization',
    },
    {
      id: 'execution',
      icon: <span className="text-lg">⚡</span>,
      name: t ? 'Ejecución' : 'Execution',
      description: t ? 'Pricing y promoción' : 'Pricing & promotion',
    },
  ];
}

export function ImplicationsPage() {
  const { t, language } = useLanguage();
  const { selectedClient } = useClient();
  const [activeSection, setActiveSection] = useState('market-segment');

  const sidebarItems = buildSidebarItems(language);

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
  };

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-text-secondary text-lg">{t.selectClient}</p>
        </div>
      </div>
    );
  }

  const clientId = selectedClient.client_id;
  const isSpanish = language === 'es';

  const activeItem = sidebarItems.find(s => s.id === activeSection);
  const contentHeader = activeItem && (
    <div className="flex items-center gap-3 mb-6">
      {activeItem.icon}
      <div>
        <h2 className="text-xl font-semibold text-white">{activeItem.name}</h2>
        <p className="text-sm text-text-secondary">{activeItem.description}</p>
      </div>
    </div>
  );

  return (
    <SidebarLayout
      title={isSpanish ? 'Implicaciones' : 'Implications'}
      subtitle={isSpanish ? 'Oportunidades Estratégicas' : 'Strategic Opportunities'}
      items={sidebarItems}
      activeItemId={activeSection}
      onItemChange={handleSectionChange}
      contentHeader={contentHeader}
    >
      <ImplicationsContent
        section={activeSection}
        clientId={clientId}
        language={language}
      />
    </SidebarLayout>
  );
}

// Main content router - Phase 7 structure
function ImplicationsContent({
  section,
  clientId,
  language,
}: {
  section: string;
  clientId: string;
  language: string;
}) {
  switch (section) {
    case 'market-segment':
      return <MarketSegmentSection clientId={clientId} language={language} />;
    case 'portfolio':
      return <PortfolioSection clientId={clientId} language={language} />;
    case 'execution':
      return <ExecutionSection clientId={clientId} language={language} />;
    default:
      return <MarketSegmentSection clientId={clientId} language={language} />;
  }
}

// ============================================================================
// MARKET & SEGMENT SECTION - Phase 7 Pattern
// ============================================================================
function MarketSegmentSection({ clientId, language }: { clientId: string; language: string }) {
  const t = language === 'es';

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['client', clientId, 'opportunities', language],
    queryFn: () => apiClient.opportunities.get(clientId, language),
    enabled: !!clientId,
  });

  if (isLoading) return <LoadingSpinner size="md" />;

  // Filter for market/segment opportunities
  const marketOpps = (opportunities?.opportunities || []).filter(
    (o) => o.type === 'segment' || o.type === 'regional' || o.type === 'distribution'
  );

  // Transform to OpportunityCard format
  const cards = marketOpps.slice(0, 6).map(opp => transformOpportunityToCard(opp, language));
  const totalPotential = calculateTotalPotential(marketOpps);

  if (cards.length === 0) {
    return (
      <div className="space-y-6">
        <InsightCard
          text={t
            ? 'No se identificaron oportunidades de mercado o segmento. Tu posición actual es sólida.'
            : 'No market or segment opportunities identified. Your current position is solid.'}
          type="success"
        />
        {/* Sample opportunities for demo */}
        <OpportunityCardGrid
          cards={getSampleMarketOpportunities(t)}
          columns={3}
          language={language as 'en' | 'es'}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚡</span>
            <div>
              <span className="text-lg font-semibold text-white">
                {cards.length} {t ? 'oportunidades identificadas' : 'opportunities identified'}
              </span>
            </div>
          </div>
          {totalPotential && (
            <div className="text-right">
              <span className="text-xs text-text-tertiary uppercase">
                {t ? 'Potencial Total' : 'Total Potential'}
              </span>
              <p className="text-lg font-semibold text-primary-400">{totalPotential}</p>
            </div>
          )}
        </div>
      </div>

      {/* Opportunity Cards Grid */}
      <OpportunityCardGrid cards={cards} columns={3} language={language as 'en' | 'es'} />
    </div>
  );
}

// ============================================================================
// PORTFOLIO SECTION - Phase 7 Pattern
// ============================================================================
function PortfolioSection({ clientId, language }: { clientId: string; language: string }) {
  const t = language === 'es';

  const { data: opportunities, isLoading: oppsLoading } = useQuery({
    queryKey: ['client', clientId, 'opportunities', language],
    queryFn: () => apiClient.opportunities.get(clientId, language),
    enabled: !!clientId,
  });

  const { data: cannibalization, isLoading: cannibLoading } = useQuery({
    queryKey: ['client', clientId, 'portfolio', 'cannibalization'],
    queryFn: () => apiClient.portfolio.getCannibalization(clientId) as unknown as Promise<CannibalizationResult>,
    enabled: !!clientId,
  });

  const { data: health } = useQuery({
    queryKey: ['client', clientId, 'portfolio', 'health'],
    queryFn: () => apiClient.portfolio.getHealth(clientId),
    enabled: !!clientId,
  });

  if (oppsLoading || cannibLoading) return <LoadingSpinner size="md" />;

  // Filter for portfolio opportunities
  const portfolioOpps = (opportunities?.opportunities || []).filter(
    (o) => o.type === 'portfolio' || o.type === 'launch' || o.type === 'innovation'
  );

  const cards = portfolioOpps.slice(0, 6).map(opp => transformOpportunityToCard(opp, language));

  // Add cannibalization and health cards if relevant
  const cannibRate = cannibalization?.total_cannibalization_rate || 0;
  const atRiskSkus = health?.at_risk_count || 0;

  if (cannibRate > 0.15) {
    cards.push({
      icon: '🔄',
      title: t ? 'Optimización por Canibalización' : 'Cannibalization Optimization',
      status: cannibRate > 0.25 ? 'review' : 'approved',
      confidence: Math.round((1 - cannibRate) * 100),
      reasons: [
        t ? `${(cannibRate * 100).toFixed(0)}% tasa de canibalización detectada` : `${(cannibRate * 100).toFixed(0)}% cannibalization rate detected`,
        t ? 'Productos con alta superposición de consumidores' : 'Products with high consumer overlap',
        t ? 'Potencial de optimización de portafolio' : 'Portfolio optimization potential',
      ],
      recommendation: t
        ? 'Considerar diferenciación de precio o descontinuación de SKUs solapados'
        : 'Consider price differentiation or discontinuation of overlapping SKUs',
      potential: t ? 'Reducir canibalización 50%' : 'Reduce cannibalization 50%',
      ctaHref: '/understand/portfolio-health',
      ctaText: t ? 'Ver Detalles' : 'View Details',
    });
  }

  if (atRiskSkus > 0) {
    cards.push({
      icon: '📉',
      title: t ? 'SKUs en Riesgo' : 'At-Risk SKUs',
      status: atRiskSkus > 3 ? 'risk' : 'review',
      confidence: Math.max(40, 100 - atRiskSkus * 15),
      reasons: [
        t ? `${atRiskSkus} SKUs con bajo desempeño` : `${atRiskSkus} underperforming SKUs`,
        t ? 'Contribución marginal a ingresos' : 'Marginal revenue contribution',
        t ? 'Potencial de racionalización' : 'Rationalization potential',
      ],
      recommendation: t
        ? 'Evaluar descontinuación o reposicionamiento de SKUs en riesgo'
        : 'Evaluate discontinuation or repositioning of at-risk SKUs',
      potential: t ? `Liberar recursos de ${atRiskSkus} SKUs` : `Free resources from ${atRiskSkus} SKUs`,
      ctaHref: '/understand/portfolio-health',
      ctaText: t ? 'Analizar' : 'Analyze',
    });
  }

  const totalPotential = calculateTotalPotential(portfolioOpps);

  if (cards.length === 0) {
    return (
      <div className="space-y-6">
        <InsightCard
          text={t
            ? 'Tu portafolio está bien optimizado. No se identificaron oportunidades críticas.'
            : 'Your portfolio is well optimized. No critical opportunities identified.'}
          type="success"
        />
        <OpportunityCardGrid
          cards={getSamplePortfolioOpportunities(t)}
          columns={3}
          language={language as 'en' | 'es'}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📦</span>
            <div>
              <span className="text-lg font-semibold text-white">
                {cards.length} {t ? 'oportunidades de portafolio' : 'portfolio opportunities'}
              </span>
            </div>
          </div>
          {totalPotential && (
            <div className="text-right">
              <span className="text-xs text-text-tertiary uppercase">
                {t ? 'Potencial Total' : 'Total Potential'}
              </span>
              <p className="text-lg font-semibold text-primary-400">{totalPotential}</p>
            </div>
          )}
        </div>
      </div>

      {/* Opportunity Cards Grid */}
      <OpportunityCardGrid cards={cards} columns={3} language={language as 'en' | 'es'} />
    </div>
  );
}

// ============================================================================
// EXECUTION SECTION - Phase 7 Pattern (Pricing & Promotion)
// ============================================================================
function ExecutionSection({ clientId, language }: { clientId: string; language: string }) {
  const t = language === 'es';

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['client', clientId, 'opportunities', language],
    queryFn: () => apiClient.opportunities.get(clientId, language),
    enabled: !!clientId,
  });

  if (isLoading) return <LoadingSpinner size="md" />;

  // Filter for execution opportunities (pricing, promotion, distribution)
  const executionOpps = (opportunities?.opportunities || []).filter(
    (o) => o.type === 'pricing' || o.type === 'promotion' || o.type === 'competitive' || o.type === 'margin'
  );

  const cards = executionOpps.slice(0, 6).map(opp => transformOpportunityToCard(opp, language));
  const totalPotential = calculateTotalPotential(executionOpps);

  if (cards.length === 0) {
    return (
      <div className="space-y-6">
        <InsightCard
          text={t
            ? 'La ejecución actual es óptima. No se identificaron oportunidades de mejora inmediata.'
            : 'Current execution is optimal. No immediate improvement opportunities identified.'}
          type="success"
        />
        <OpportunityCardGrid
          cards={getSampleExecutionOpportunities(t)}
          columns={3}
          language={language as 'en' | 'es'}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚡</span>
            <div>
              <span className="text-lg font-semibold text-white">
                {cards.length} {t ? 'oportunidades de ejecución' : 'execution opportunities'}
              </span>
            </div>
          </div>
          {totalPotential && (
            <div className="text-right">
              <span className="text-xs text-text-tertiary uppercase">
                {t ? 'Potencial Total' : 'Total Potential'}
              </span>
              <p className="text-lg font-semibold text-primary-400">{totalPotential}</p>
            </div>
          )}
        </div>
      </div>

      {/* Opportunity Cards Grid */}
      <OpportunityCardGrid cards={cards} columns={3} language={language as 'en' | 'es'} />
    </div>
  );
}


// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Transform API opportunity to OpportunityCard props
interface OpportunityCardProps {
  icon?: string;
  title: string;
  status: StatusType;
  confidence: number;
  reasons: string[];
  recommendation: string;
  potential?: string;
  ctaText?: string;
  ctaHref?: string;
}

function transformOpportunityToCard(opp: EvaluatedOpportunity, language: string): OpportunityCardProps {
  const t = language === 'es';

  // Map impact to status
  const statusMap: Record<string, StatusType> = {
    critical: 'risk',
    high: 'review',
    medium: 'approved',
    low: 'approved',
  };

  // Convert severity score to confidence (inverse relationship)
  const confidence = Math.max(40, Math.min(95, 100 - (opp.severityScore || 50)));

  // Build reasons from available data
  const reasons: string[] = [];
  if (opp.description) {
    reasons.push(opp.description);
  }
  if (opp.evidence) {
    reasons.push(opp.evidence);
  }
  if (opp.affectedProducts && opp.affectedProducts.length > 0) {
    reasons.push(
      t
        ? `Afecta ${opp.affectedProducts.length} productos`
        : `Affects ${opp.affectedProducts.length} products`
    );
  }

  return {
    icon: opp.icon || '💡',
    title: opp.title,
    status: statusMap[opp.impact] || 'approved',
    confidence,
    reasons: reasons.slice(0, 3),
    recommendation: opp.action || (t ? 'Revisar y tomar acción' : 'Review and take action'),
    potential: opp.impact === 'critical' || opp.impact === 'high'
      ? (t ? 'Alto impacto' : 'High impact')
      : undefined,
    ctaText: opp.type === 'pricing' ? (t ? 'Simular' : 'Simulate') : (t ? 'Ver Detalles' : 'View Details'),
    ctaHref: opp.type === 'pricing' ? '/decide' : '/understand/portfolio-health',
  };
}

// Calculate total potential from opportunities
function calculateTotalPotential(opportunities: EvaluatedOpportunity[]): string | undefined {
  const criticalCount = opportunities.filter(o => o.impact === 'critical').length;
  const highCount = opportunities.filter(o => o.impact === 'high').length;

  if (criticalCount + highCount === 0) return undefined;

  // Estimate potential based on opportunity count and impact
  const estimatedValue = criticalCount * 2.5 + highCount * 1.2;
  return `$${estimatedValue.toFixed(1)}M`;
}

// Sample opportunities for when no real data exists
function getSampleMarketOpportunities(t: boolean): OpportunityCardProps[] {
  return [
    {
      icon: '🎯',
      title: t ? 'Expansión de Segmento Premium' : 'Premium Segment Expansion',
      status: 'approved',
      confidence: 88,
      reasons: [
        t ? 'Segmento crece 12% anual' : 'Segment growing 12% annually',
        t ? 'Baja participación actual (8%)' : 'Low current share (8%)',
        t ? 'Alto margen de contribución' : 'High contribution margin',
      ],
      recommendation: t
        ? 'Desarrollar línea premium con diferenciación clara'
        : 'Develop premium line with clear differentiation',
      potential: '+$3.2M',
      ctaText: t ? 'Simular Lanzamiento' : 'Simulate Launch',
      ctaHref: '/decide/launch',
    },
    {
      icon: '🏪',
      title: t ? 'Penetración Canal Tradicional' : 'Traditional Channel Penetration',
      status: 'approved',
      confidence: 82,
      reasons: [
        t ? '28% del mercado en tradicional' : '28% of market in traditional',
        t ? 'Solo 15% de tu distribución' : 'Only 15% of your distribution',
        t ? 'Competencia débil en canal' : 'Weak competition in channel',
      ],
      recommendation: t
        ? 'Expandir cobertura en tiendas tradicionales'
        : 'Expand coverage in traditional stores',
      potential: '+$2.4M',
      ctaText: t ? 'Ver Análisis' : 'View Analysis',
      ctaHref: '/understand/market-landscape',
    },
    {
      icon: '👥',
      title: t ? 'Segmento Value Hunters' : 'Value Hunters Segment',
      status: 'review',
      confidence: 72,
      reasons: [
        t ? '35% del mercado total' : '35% of total market',
        t ? 'Alta sensibilidad a promociones' : 'High promo sensitivity',
        t ? 'Baja lealtad de marca' : 'Low brand loyalty',
      ],
      recommendation: t
        ? 'Desarrollar estrategia promocional targeted'
        : 'Develop targeted promotional strategy',
      potential: '+$1.8M',
      ctaText: t ? 'Ver Segmentos' : 'View Segments',
      ctaHref: '/understand/consumer-intelligence',
    },
  ];
}

function getSamplePortfolioOpportunities(t: boolean): OpportunityCardProps[] {
  return [
    {
      icon: '🚀',
      title: t ? 'Lanzamiento de Extensión de Línea' : 'Line Extension Launch',
      status: 'approved',
      confidence: 91,
      reasons: [
        t ? 'Gap identificado en portafolio' : 'Portfolio gap identified',
        t ? 'Alta afinidad con consumidores actuales' : 'High affinity with current consumers',
        t ? 'Benchmark exitoso en otros mercados' : 'Successful benchmark in other markets',
      ],
      recommendation: t
        ? 'Proceder con piloto de lanzamiento en Q2'
        : 'Proceed with launch pilot in Q2',
      potential: '+$4.1M',
      ctaText: t ? 'Simular Lanzamiento' : 'Simulate Launch',
      ctaHref: '/decide/launch',
    },
    {
      icon: '📊',
      title: t ? 'Racionalización de SKUs' : 'SKU Rationalization',
      status: 'review',
      confidence: 78,
      reasons: [
        t ? '4 SKUs con bajo desempeño' : '4 underperforming SKUs',
        t ? 'Contribución marginal a ingresos' : 'Marginal revenue contribution',
        t ? 'Complejidad operativa evitable' : 'Avoidable operational complexity',
      ],
      recommendation: t
        ? 'Evaluar descontinuación gradual de SKUs de bajo rendimiento'
        : 'Evaluate gradual discontinuation of low-performing SKUs',
      potential: t ? 'Reducir complejidad 20%' : 'Reduce complexity 20%',
      ctaText: t ? 'Ver Portfolio' : 'View Portfolio',
      ctaHref: '/understand/portfolio-health',
    },
    {
      icon: '🔄',
      title: t ? 'Optimización de Mix' : 'Mix Optimization',
      status: 'approved',
      confidence: 85,
      reasons: [
        t ? 'Mix actual sub-óptimo vs. potencial' : 'Current mix suboptimal vs potential',
        t ? 'Oportunidad de trade-up' : 'Trade-up opportunity',
        t ? 'Margen incremental +8%' : 'Incremental margin +8%',
      ],
      recommendation: t
        ? 'Ajustar distribución numérica hacia SKUs premium'
        : 'Adjust numeric distribution toward premium SKUs',
      potential: '+$1.5M',
      ctaText: t ? 'Simular' : 'Simulate',
      ctaHref: '/decide',
    },
  ];
}

function getSampleExecutionOpportunities(t: boolean): OpportunityCardProps[] {
  return [
    {
      icon: '💰',
      title: t ? 'Optimización de Precio' : 'Pricing Optimization',
      status: 'approved',
      confidence: 88,
      reasons: [
        t ? 'Elasticidad favorable (-0.8)' : 'Favorable elasticity (-0.8)',
        t ? 'Competencia no ha ajustado precios' : 'Competition has not adjusted prices',
        t ? 'Temporada alta próxima' : 'High season approaching',
      ],
      recommendation: t
        ? 'Considerar incremento de 3-5% en productos premium'
        : 'Consider 3-5% increase on premium products',
      potential: '+$3.1M',
      ctaText: t ? 'Simular Precio' : 'Simulate Price',
      ctaHref: '/decide',
    },
    {
      icon: '🎁',
      title: t ? 'Efectividad Promocional' : 'Promotional Effectiveness',
      status: 'review',
      confidence: 65,
      reasons: [
        t ? 'ROI actual 1.2x vs benchmark 1.8x' : 'Current ROI 1.2x vs benchmark 1.8x',
        t ? 'Alta inversión en promos ineficientes' : 'High investment in inefficient promos',
        t ? 'Oportunidad de reasignar presupuesto' : 'Budget reallocation opportunity',
      ],
      recommendation: t
        ? 'Reducir promociones de bajo ROI y reasignar a canales efectivos'
        : 'Reduce low-ROI promotions and reallocate to effective channels',
      potential: '+$1.8M',
      ctaText: t ? 'Analizar' : 'Analyze',
      ctaHref: '/implications/performance-diagnosis',
    },
    {
      icon: '🏪',
      title: t ? 'Cobertura de Distribución' : 'Distribution Coverage',
      status: 'approved',
      confidence: 82,
      reasons: [
        t ? 'Distribución numérica 65% vs. 85% objetivo' : 'Numeric distribution 65% vs 85% target',
        t ? 'Gaps en canal tradicional' : 'Gaps in traditional channel',
        t ? 'Competencia tiene mejor cobertura' : 'Competition has better coverage',
      ],
      recommendation: t
        ? 'Expandir cobertura en top 500 tiendas tradicionales'
        : 'Expand coverage in top 500 traditional stores',
      potential: '+$2.4M',
      ctaText: t ? 'Ver Canales' : 'View Channels',
      ctaHref: '/understand/market-landscape',
    },
  ];
}

